import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  createStyles,
  FormControlLabel,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import _ from "lodash";
import WithLoading from "../../../../utils/WithLoading";
import { CustomTheme } from "../../../../types/customTheme";
import { handleDownloadPdf } from "./convert/handlePdf";
import { handleDownloadExcel } from "./convert/handleExcel";
import LocationDetailsCard from "../../../../components/common/LocationDetailsCard";
import MenuButtonCommon from "../../../../components/buttons/MenuButtonCommon";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import SwitchCommon from "../../../../components/switches/SwitchCommon";
import NormalSalesTable from "./NormalSalesTable";
import { handleDownloadExcelNew } from "./convert/handleExcelNew";
import { handleDownloadPdfNew } from "./convert/handlePdfNew";
import ChartSalesTableYearly from "./ChartSalesTableYearly";
import ChartSalesTable from "./ChartSalesTable";

type InputData = Record<
  string,
  { week: number; saleCount: number; saleTotal: number }[]
>;
type OutputData = {
  totalCount: number[];
  totalValue: number[];
  structure: Record<
    string,
    { week: number; data: { months: string; count: number; value: number }[] }[]
  >;
};

export interface ShiftHeaderProps {
  isLoading: any;
  startDate: any;
  endDate: any;
  filterDetails: any;
  locationSelectedList: any;
  handleOnClickText: any;
  driverSummaryNodeList: any;
  weeklySales: any;
  weeklySalesCountValue: any;
  weeklySalesTotalValue: any;
}
const driverSummaryNodeList = {
  "2025": [
    {
      month: 8,
      saleCount: 42,
      saleTotal: 230.49,
    },
  ],
};
interface Sale {
  week: number;
  saleCount: number;
  saleTotal: number;
}

interface OutputWeek {
  week: number;
  data: { months: string; count: number; value: number }[];
}

const dropDownDaysList = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const useStyles = makeStyles((theme: any) =>
  createStyles({
    tableContainer: {
      overflowX: "auto", // Enable horizontal scroll if needed
      backgroundColor: "transparent",
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: "4px", // Adjust spacing here (horizontal vertical)
    },

    dateCell: {
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      padding: "8px",
      borderBottom: "none",
    },
    hourCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default, // Ensure background is solid for fixed column
      zIndex: 1, // Ensure it stays on top of other cells when scrolling
      fontWeight: "bold",
      borderBottom: "none",
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
    hourHeaderCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default,
      zIndex: 3, // Ensure the header stays on top
      fontWeight: "bold",
      borderBottom: "none",
    },
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_highlight_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

/**
 * Get all ISO week numbers in a date range.
 * @param startDate - e.g., "2025-01-04"
 * @param endDate - e.g., "2025-01-19"
 * @returns array of week numbers, e.g., [1, 2, 3]
 */
const getMonthsByRange = (
  startDate: string,
  endDate: string,
): Record<number, string[]> => {
  const result: Record<number, string[]> = {};
  let current = new Date(startDate + "T00:00:00Z");
  const end = new Date(endDate + "T00:00:00Z");

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  while (current <= end) {
    const year = current.getUTCFullYear();
    const monthName = monthNames[current.getUTCMonth()];

    if (!result[year]) result[year] = [];
    if (!result[year].includes(monthName)) result[year].push(monthName);

    // move to next day
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return result;
};

/**
 * Generate weekly sales report between two dates
 */
function generateWeeklyReport(input: any, startDate: any, endDate: any) {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const result = [];
  // Totals
  let totalCard = 0;
  let totalCash = 0;
  let totalDiscount = 0;
  let totalOtherPayments = 0;
  let totalCount = 0;
  let totalSaleTotal = 0;
  let totalAmount = 0;

  // Helper: get ISO week number
  function getWeekNumber(d: any) {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart: any = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  }

  // Iterate week by week
  let current = new Date(start);

  while (current <= end) {
    const year = current.getFullYear();
    const week = getWeekNumber(current); // ISO week number
    const monthName = current.toLocaleString("en-US", { month: "long" });

    const found = input[year]?.find((i: any) => i.week === week);

    const card = found ? found.card : 0;
    const cash = found ? found.cash : 0;
    const discount = found ? found.discount : 0;
    const otherPayments = found ? found.otherPayments : 0;
    const saleCount = found ? found.saleCount : 0;
    const saleTotal =
      (found ? found.saleTotal : 0) - (found ? found.discount : 0);
    const total = found ? found.total : 0;

    // ✅ accumulate totals
    totalCard += card;
    totalCash += cash;
    totalDiscount += discount;
    totalOtherPayments += otherPayments;
    totalCount += saleCount;
    totalSaleTotal += saleTotal;
    totalAmount += total;

    result.push({
      week: `${year} ${monthName} (Week ${week})`,
      card,
      cash,
      discount,
      otherPayments,
      saleCount,
      saleTotal,
      total,
    });

    // move to next week
    current.setDate(current.getDate() + 7);
  }

  // Add grand total row
  result.push({
    week: "Total",
    card: Number(totalCard.toFixed(2)),
    cash: Number(totalCash.toFixed(2)),
    discount: Number(totalDiscount.toFixed(2)),
    otherPayments: Number(totalOtherPayments.toFixed(2)),
    saleCount: totalCount,
    saleTotal: Number(totalSaleTotal.toFixed(2)),
    total: Number(totalAmount.toFixed(2)),
  });

  return result;
}

/**
 * HourlySalesReportNode Component:
 *
 * This component handles the header section of the Shift component, including
 * sorting controls, location selection, and shift generation. It displays shift
 * information in a table-like format with sortable columns and provides options
 * for selecting shifts, generating reports, and handling user interactions.
 */
const HourlySalesReportNode: React.FunctionComponent<ShiftHeaderProps> = ({
  isLoading,
  startDate,
  endDate,
  filterDetails,
  locationSelectedList,
  handleOnClickText,
  driverSummaryNodeList,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRoleObj, setSelectedRoleObj] = useState<any>({
    id: "all",
    label: "Sales Count & Total",
  });
  const [isShowChart, setIsShowChart] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedDaysList, setSelectedDaysList] =
    useState<any>(dropDownDaysList);
  // New state for the yearly average toggle
  const [showYearlyAverage, setShowYearlyAverage] = useState(false);

  const days = getMonthsByRange(startDate, endDate);

  const openExport = Boolean(anchorEl);

  function groupWeeksByMonthsWithRange(
    input: Record<string, any[]>,
    allWeeks: Record<string, string[]>,
    startDate: string,
    endDate: string,
  ): OutputData {
    const result: OutputData = {
      totalCount: [],
      totalValue: [],
      structure: {},
    };

    const start = new Date(startDate + "T00:00:00Z");
    const end = new Date(endDate + "T00:00:00Z");

    // ISO week calculation
    const getISOWeekAndYear = (d: Date) => {
      const date = new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()),
      );
      const dayNum = date.getUTCDay() || 7; // Monday = 1, Sunday = 7
      date.setUTCDate(date.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
      const week = Math.ceil(
        ((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
      );
      const isoYear = date.getUTCFullYear();
      return { week, isoYear };
    };

    // Build month-year mapping
    const monthsWithYear: { month: string; year: string }[] = [];
    Object.keys(allWeeks).forEach((y) => {
      allWeeks[y].forEach((m) => monthsWithYear.push({ month: m, year: y }));
    });

    Object.keys(allWeeks).forEach((yearStr) => {
      const year = parseInt(yearStr);
      const sales = input[yearStr] || [];

      // Collect weeks in this year
      const weeksInYear: number[] = [];
      let current = new Date(start);
      while (current <= end) {
        const { week, isoYear } = getISOWeekAndYear(current);
        if (isoYear === year && !weeksInYear.includes(week))
          weeksInYear.push(week);
        current.setUTCDate(current.getUTCDate() + 1);
      }
      weeksInYear.sort((a, b) => a - b);

      // Map weeks to structure
      result.structure[yearStr] = weeksInYear.map((week) => {
        const weekData = monthsWithYear.map(({ month, year: monthYear }) => {
          // Only use sale if year matches and month matches week start
          let count = 0,
            value = 0;
          if (monthYear === yearStr) {
            const sale = sales.find((s) => s.week === week);
            if (sale) {
              // Get month of week start
              const simple = new Date(
                Date.UTC(parseInt(yearStr), 0, 1 + (week - 1) * 7),
              );
              const dayOfWeek = simple.getUTCDay();
              const diff = (dayOfWeek <= 0 ? -6 : 1) - dayOfWeek;
              const weekStart = new Date(simple);
              weekStart.setUTCDate(simple.getUTCDate() + diff);
              const weekMonth = weekStart.toLocaleString("default", {
                month: "long",
              });
              if (weekMonth === month) {
                count = sale.saleCount;
                value = sale.saleTotal;
              }
            }
          }
          result.totalCount.push(count);
          result.totalValue.push(value);
          return { months: month, count, value };
        });
        return { week, data: weekData };
      });
    });

    return result;
  }
  const [value, setValue] = useState<any>([]);
  const [dummyData, setDummyData] = useState<any>([]);
  const [valueTotal, setValueTotal] = useState<any>([]);
  const [nodeData, setNodeData] = useState<any>([]);

  useEffect(() => {
    const data = generateWeeklyReport(
      driverSummaryNodeList,
      startDate,
      endDate,
    );
    setNodeData(data);
    const { structure, totalCount, totalValue } = groupWeeksByMonthsWithRange(
      driverSummaryNodeList,
      days,
      startDate,
      endDate,
    );
    const uniqueSortedCount = Array.from(
      new Set(totalCount.filter((v: any) => v !== 0)),
    ).sort((a: any, b: any) => a - b);
    const uniqueSortedValue = Array.from(
      new Set(totalValue.filter((v: any) => v !== 0)),
    ).sort((a: any, b: any) => a - b);
    setDummyData(structure);
    setValue(uniqueSortedCount);
    setValueTotal(uniqueSortedValue);
  }, [
    driverSummaryNodeList,
    selectedDaysList,
    selectedRoleObj,
    startDate,
    endDate,
  ]);
  const theme: CustomTheme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const daysWeekly = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const handleShowChart = () => {
    setIsShowChart(!isShowChart);
  };
  // New handler for the yearly average toggle
  const handleShowYearlyAverage = () => {
    setShowYearlyAverage(!showYearlyAverage);
  };

  const maxWidthLocationChips = useMediaQuery("(max-width: 600px)");

  return (
    <>
      <Grid
        container
        style={{
          marginBottom: "20px",
          marginTop: "8px",
        }}
      >
        <Grid item xs={12}>
          <div
            style={
              maxWidthLocationChips
                ? { display: "block" }
                : {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }
            }
          >
            <LocationDetailsCard
              locationSelectedList={locationSelectedList}
              handleOnClickText={handleOnClickText}
            />
            <div style={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                style={{ marginRight: "8px" }}
                control={<SwitchCommon />}
                checked={isShowChart}
                onChange={handleShowChart}
                label="Show Heatmap"
                labelPlacement="start"
              />
              {/* New toggle for yearly average, only shown for the chart view */}
              {isShowChart && (
                <FormControlLabel
                  style={{ marginRight: "8px" }}
                  control={<SwitchCommon />}
                  checked={showYearlyAverage}
                  onChange={handleShowYearlyAverage}
                  label="Show Yearly Average"
                  labelPlacement="start"
                />
              )}
              {true && (
                <div
                  style={
                    maxWidthLocationChips
                      ? {
                          display: "flex",
                          justifyContent: "end",
                          marginTop: "8px",
                        }
                      : { marginLeft: "16px" }
                  }
                >
                  <MenuButtonCommon
                    id="basic-button"
                    variant="contained"
                    style={{
                      fontSize: 11,
                      height: "38px",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                    }}
                    color="blue"
                    aria-controls={open ? "basic-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                  >
                    <GetAppIcon />
                    <Typography
                      variant="body1"
                      style={{ paddingLeft: "16px", color: "white" }}
                    >
                      Export
                    </Typography>
                  </MenuButtonCommon>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={openExport}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "basic-button",
                    }}
                    PaperProps={{
                      elevation: 0,
                      style: {
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.background.entity_border}`,
                      },
                    }}
                    style={{ top: "56px" }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleDownloadPdfNew(
                          [
                            "Month",
                            "Orders",
                            "Sub Total",
                            "Discount",
                            "Total",
                            "Cash Pay",
                            "Card",
                            "Other Pay",
                          ],
                          nodeData,
                          filterDetails,
                        );
                        handleClose();
                      }}
                    >
                      Export as PDF
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleDownloadExcelNew(
                          [
                            "Month",
                            "Orders",
                            "Sub Total",
                            "Total",
                            "Discount",
                            "Cash Pay",
                            "Card",
                            "Other Pay",
                          ],
                          nodeData,
                          filterDetails,
                        );
                        handleClose();
                      }}
                    >
                      Export as CSV
                    </MenuItem>
                  </Menu>
                </div>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
      <TableContainer className={classes.tableContainer}>
        {isShowChart ? (
          showYearlyAverage ? (
            <ChartSalesTableYearly nodes={nodeData} />
          ) : (
            <ChartSalesTable nodes={nodeData} />
          )
        ) : (
          <NormalSalesTable
            days={["week", "saleCount", "saleTotal"]}
            dummyData={nodeData}
          />
        )}
      </TableContainer>
    </>
  );
};

export default WithLoading(HourlySalesReportNode);
